import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useApi } from "./api";
import { BASE_APP_PAth } from "./api/launchpad-api";
import { CommercialQuote, SaveQuoteRequest } from "./api/types/quote-request";
import { Navbar } from "./components/navbar";
import { CommercialProduct } from "./config/types/product-config-settings";
import { GeicoPageNames, IGeicoDtm, useAnalytics } from "./contexts/AnalyticsContext";
import { useClientConfigs } from "./contexts/ClientConfigContext";
import store, { RootState } from "./store";
import { clearCache, updateQuote, InitQuoteCache, updateQuoteResponse } from "./store/quote/action";
import { ProtectedRouter } from "./util/ProtectedRouter";
import { UnAuthorised } from "./pages/un-authroised/UnAuthorised";
import { ZipSelection } from "./pages/zip-selection/zip-selection";
import { ProductSelection } from "./pages/product-selection/product-selection";
import { ProductSelectionUkw } from "./pages/product-selection/product-selection-ukw";
import { useAutoRedirectContext } from "./contexts/AutoRedirectContext";
import { useAuthContext } from "./contexts/AuthContextProvider";
import { usePageLoadingContext } from "./contexts/PageLoadingContext";
import { BusinessKeyword } from "./pages/business-keyword/business-keyword";
import { BusinessKeywordAgent } from "./pages/business-keyword/business-keyword-agent";
import { BusinessKeywordAgentUkw } from "./pages/business-keyword/business-keyword-agent-ukw";
import { AgentUpsertQuoteInfo } from "./pages/agent-upsert-quote-info/agent-upsert-quote-info";
import { SearchQuote } from "./pages/search-quote/search-quote";
import ConsolidatedSummaryComponent from "./components/consolidated-summary-pages";
import { KeywordRecommendation } from "./pages/experiment/keyword-recomendation";
import { AgentPartnerSelect } from "./pages/agent-partner-select/agent-partner-select";
import { QuoteNotice } from "./pages/quote-notice/quote-notice";
import { InsuranceRelatedPartners } from "./pages/insurance-related-partners/insurance-related-partners";
import { AdminLayout } from "./components/admin/AdminLayout";
import { AdminDashboard, AppetiteMapping, ApprovalQueue, AuditLogs, BulkOperations } from "./pages/admin";
import { getAdminAccessRoleIds } from "./config/admin-roles";
import { shouldBypassQuoteFunnelRedirect } from "./utils/quote-funnel-redirect";

export const App = () => {
    const { geicoDtm, sendToAnalytics, setGeicoDtm } = useAnalytics();
    const clientConnfig = useClientConfigs();
    const { hideLoader } = usePageLoadingContext();
    const quote = useSelector((state: RootState) => state.quote.quote);
    const redirectContext = useAutoRedirectContext();
    const navigate = useNavigate();
    const location = useLocation();
    const api = useApi();
    const authContext = useAuthContext();
    const [showUI, setShowUI] = useState(false);
    const isPopstateListenerAdded = useRef(false); // Ref to store the flag
    const [isSummaryVisible, setIsSummaryVisible] = useState(true);
    const [hasInitialized, setHasInitialized] = useState(false);

    const saveUserTypeInRedux = () => {
        const employeeId = authContext.getEmployeeId();
        const userType = authContext.getUserType();
        const saveQuote = new SaveQuoteRequest();
        saveQuote.UserType = userType;
        saveQuote.UserId = employeeId;
        store.dispatch(InitQuoteCache(saveQuote));
    }

    const startNewSession = (selectedZip: string | null, selectedStateCode: string | null, zipVerified: boolean, selectedProduct: CommercialProduct | undefined): void => {
        const newQuote = new CommercialQuote();
        newQuote.Zip = selectedZip && selectedZip.length <= 5 ? selectedZip : quote.data.Zip;
        newQuote.StateCode = selectedStateCode && selectedStateCode.length === 2 ? selectedStateCode : quote.data.StateCode;

        newQuote.Soa = quote.data.Soa === null || quote.data.Soa === "" ? redirectContext.soa : quote.data.Soa;
        newQuote.LaunchpadId = quote.data.LaunchpadId;
        newQuote.SourceCorrelationId = quote.data.SourceCorrelationId === null || quote.data.SourceCorrelationId === ""
            ? redirectContext.sourceCorrelationId : quote.data.SourceCorrelationId;

        newQuote.SelectedProducts = selectedProduct === undefined ? quote.data.SelectedProducts :
            [
                {
                    LobCode: selectedProduct.lobCode,
                    LobName: selectedProduct.lobName,
                    SspCode: selectedProduct.sspCode,
                    AdobeDtmName: selectedProduct.adobeDtmName
                }];
        newQuote.BusinessKeyword = quote.data.BusinessKeyword;

        saveUserTypeInRedux();
        store.dispatch(updateQuote({ ...newQuote }));
        setShowUI(true);
        hideLoader();
    }

    const autoSaveAndRedirect = (isComplete: boolean) => {

        const selectedProducts = redirectContext.product == undefined ? [] :
            [
                {
                    LobCode: redirectContext.product.lobCode,
                    LobName: redirectContext.product.lobName,
                    SspCode: redirectContext.product.sspCode,
                    AdobeDtmName: redirectContext.product.adobeDtmName
                }];

        api.commercialAgentApi.saveQuote({
            ...quote.data,
            Zip: redirectContext.zipCode,
            StateCode: redirectContext.stateCode,
            SelectedProducts: selectedProducts,
            Soa: redirectContext.soa,
            SourceCorrelationId: redirectContext.sourceCorrelationId,
            LaunchpadId: redirectContext.launchpadId ?? "",
        }, isComplete).then((quoteResponse) => {
            if (quoteResponse.type === "SAVE_QUOTE_RESPONSE") {
                let redirectURL = quoteResponse.data.redirectUrl;
                if (isComplete && redirectURL && redirectURL.length > 0) {
                    store.dispatch(clearCache());

                    // Adobe Geico DTM Track call
                    // Init adobe Dtm
                    const adobeDtm: IGeicoDtm = {
                        ...geicoDtm,
                        zip: quote.data.Zip,
                        state: quote.data.StateCode,
                        product: redirectContext.product?.adobeDtmName
                    };
                    setGeicoDtm(adobeDtm);
                    sendToAnalytics(false);
                    // End of Adobe DTM
                    window.location.replace(redirectURL);
                }
                else {
                    store.dispatch(updateQuote({
                        ...quote.data,
                        LaunchpadId: redirectContext.launchpadId ?? quoteResponse.data.launchpadId,
                        Soa: redirectContext.soa,
                        SourceCorrelationId: redirectContext.sourceCorrelationId,
                        StateCode: redirectContext.stateCode,
                        Zip: redirectContext.zipCode,
                        SelectedProducts: selectedProducts
                    }));

                    setShowUI(true);
                }

            }
            else if (quoteResponse.type === "API_ERROR_RESPONSE") {
                setShowUI(true);
            }
        }, (error) => {
            console.error(error);
            setShowUI(true);
        });
    }

    const getCookie = (name: string): string | null => {
        const cookies = document.cookie.split(';').map(cookie => cookie.trim());
        const cookie = cookies.find(c => c.startsWith(`${name}=`));
        if (!cookie) return null;
        
        const value = cookie.substring(name.length + 1);
        return decodeURIComponent(value);
    };

    useEffect(() => {
        setGeicoDtm({ ...geicoDtm, viewName: GeicoPageNames.Launchpad });
        const soaCookie = getCookie("soa");
        if (soaCookie && soaCookie.trim() !== "") {
            setGeicoDtm({
                ...geicoDtm,
                soa: soaCookie.trim()
            });
        }
    }, []);

    useEffect(() => {
        let soaValue = "";
        if (redirectContext.soa && redirectContext.soa.trim() !== "") {
            soaValue = redirectContext.soa.trim();
        } else {
            const soaCookie = getCookie("soa");
            if (soaCookie && soaCookie.trim() !== "") {
                soaValue = soaCookie.trim();
            }
        }

        if (soaValue !== "") {
            setGeicoDtm({
                ...geicoDtm,
                soa: soaValue
            });
        }
    }, [redirectContext.soa]);

    useEffect(() => {
        if (!isPopstateListenerAdded.current) {

            // Handle browser back button 
            const handleBackButton = (event: PopStateEvent) => {
                store.dispatch(clearCache());
                if (!quote?.data?.Zip
                    || !quote.data.StateCode
                    || quote.data.Zip?.length !== 5
                    || quote.data.StateCode?.length !== 2
                ) {
                    const targetPath = clientConnfig.isRecallEnabled ? "/search-quote" : "/zip-selection";
                    navigate(BASE_APP_PAth + targetPath, { replace: true });
                    return;
                }
                if (!quote.data.SelectedProducts || quote.data.SelectedProducts.length < 1) {
                    navigate(BASE_APP_PAth + "/product-selection", { replace: true });
                }
            };
            window.addEventListener('popstate', handleBackButton);
            isPopstateListenerAdded.current = true; // Set the flag to true
            return () => {
                window.removeEventListener('popstate', handleBackButton);
                isPopstateListenerAdded.current = false; // Reset the flag
            };
        }
    }, []);

    useEffect(() => {
        
        let saveAndAutoRedirect = true;
        let isComplete = true;

        if (["BOP", "GL", "WC", "PL"].some(p => p.toLocaleLowerCase() === redirectContext?.product?.lobCode.toLocaleLowerCase())) {
            saveAndAutoRedirect = false;
            if(redirectContext.launchpadId){
                saveAndAutoRedirect = true;
                isComplete = false;
            }
        }

        if (clientConnfig.configLoaded
            && redirectContext.verificationDone
            && !hasInitialized) {
            setHasInitialized(true);

            if (redirectContext.isAutoRedirect
                && clientConnfig.isAutoRedirectEnabled
                && saveAndAutoRedirect) {
                autoSaveAndRedirect(isComplete);
            }
            else {
                startNewSession(redirectContext.zipCode, redirectContext.stateCode, true, redirectContext.product);
            }
        }
    }, [clientConnfig.configLoaded, redirectContext, hasInitialized]);

    // Set path when show UI
    useEffect(() => {
        if (showUI) {
            // Static/legal pages must not be forced into the quote funnel (e.g. new-tab partners link)
            if (shouldBypassQuoteFunnelRedirect(location.pathname)) {
                return;
            }

            if (redirectContext.verificationDone
                && redirectContext.zipCode?.length === 5
                && redirectContext.stateCode?.length === 2
            ) {
                navigate(BASE_APP_PAth + "/product-selection", { replace: true });
                return;
            }


            if (redirectContext.verificationDone
                && redirectContext.zipCode?.length === 5
                && redirectContext.stateCode?.length === 2
                && redirectContext.product
                && ["BOP", "GL"].some(p => p.toLocaleLowerCase() === redirectContext.product?.lobCode.toLocaleLowerCase())
            ) {
                navigate(BASE_APP_PAth + "/business-keyword", { replace: true });
                return;
            }

            if (authContext.getUserType() === "Agent" && clientConnfig.isRecallEnabled) {
                navigate(BASE_APP_PAth + "/search-quote", { replace: true });
                return;
            }
            if (redirectContext.verificationDone
                || !redirectContext.zipCode
                || !redirectContext.stateCode
                || redirectContext.zipCode?.length !== 5
                || redirectContext.stateCode?.length !== 2
            ) {
                const targetPath = clientConnfig.isRecallEnabled ? "/search-quote" : "/zip-selection";
                navigate(BASE_APP_PAth + targetPath, { replace: true });
                return;
            }

            if (!quote.data
                || !quote.data.Zip
                || !quote.data.StateCode
                || quote.data.Zip?.length !== 5
                || quote.data.StateCode?.length !== 2
            ) {
                const targetPath = clientConnfig.isRecallEnabled ? "/search-quote" : "/zip-selection";
                navigate(BASE_APP_PAth + targetPath, { replace: true });
                return;
            }
        }
    }, [showUI]);

    // First time loading
    if (!clientConnfig.configLoaded || !showUI)
        return null;

    const handleResetForm = () => {
        setIsSummaryVisible(false);
    };

    const isAdminPage = location.pathname.toLowerCase().includes("business-insurance/admin");
    const isPartnersPage = shouldBypassQuoteFunnelRedirect(location.pathname);

    return (
        <div className="main-wrapper">
            {!isPartnersPage && <Navbar />}
            {/* <div id="title-bar" className="fixed top-0  left-0 right-0 py-0 bg-white shadow-md">Geico Commercial Service </div> */}
            <main id="wrapper" role="main" className="bg-color--cloudy">
                {
                    clientConnfig.userType === "Agent" && !isPartnersPage
                        ? <div id="title-bar">Commercial Launchpad (Agent)</div>
                        : null
                }
                {clientConnfig.userType === "Agent" && isSummaryVisible && !isAdminPage && !isPartnersPage && <ConsolidatedSummaryComponent onResetForm={handleResetForm} />}
                {
                    <Routes>
                        <Route element={<ProtectedRouter roles={["commercial_launchpad_agent", "GFR"]} />}>
                            <Route path="*" element={<Navigate replace to={{
                                pathname: "business-insurance",
                                search: window.location.search.toLowerCase()
                            }} />} />

                            <Route path="business-insurance" element={<></>} />
                            {/* <Route path="business-insurance/zip-selection" element={<ZipSelection />} /> */}
                            <Route path="business-insurance/zip-selection" element={clientConnfig.userType === "Agent" && clientConnfig.isCombinedProductPageEnabled ? <AgentPartnerSelect /> : <ZipSelection />} />
                            {/* <Route path="business-insurance/product-selection" element={<ProductSelection />} /> */}
                            <Route path="business-insurance/product-selection"
                                element={
                                    clientConnfig.userType === "Agent"
                                        && clientConnfig.isUniversalKeywordEnabled
                                        ? clientConnfig.isCombinedProductPageEnabled ? <AgentPartnerSelect /> : <ProductSelectionUkw />
                                        : <ProductSelection />
                                }
                            />
                            {/* <Route path="business-insurance/business-keyword" element={<BusinessKeyword />} />   */}
                            <Route path="business-insurance/business-keyword"
                                element={
                                    clientConnfig.userType === "Agent"
                                        && clientConnfig.isMultiplePartnerRecommendationEnabled
                                        && clientConnfig.isUniversalKeywordEnabled
                                        ? clientConnfig.isCombinedProductPageEnabled ? <AgentPartnerSelect /> : <BusinessKeywordAgentUkw />
                                        : clientConnfig.userType === "Agent"
                                            && clientConnfig.isMultiplePartnerRecommendationEnabled
                                            ? <BusinessKeywordAgent />
                                            : clientConnfig.isBusinessKeywordRecommendationEnabled
                                                ? <KeywordRecommendation />
                                                : <BusinessKeyword />
                                }
                            />
                            {clientConnfig.isRecallEnabled && (
                                <>
                                    <Route path="business-insurance/agent-upsert-quote" element={<AgentUpsertQuoteInfo />} />
                                    <Route path="business-insurance/search-quote" element={<SearchQuote startNewSession={startNewSession} />} />
                                    {/* <Route path="business-insurance/quote-summary" element={<MktPlaceQuoteSummary />} /> */}
                                </>
                            )}
                            <Route path="business-insurance/quote-notice" element={<QuoteNotice />} />
                            <Route path="business-insurance/insurance-related-partners" element={<InsuranceRelatedPartners />} />
                        </Route>

                        <Route element={<ProtectedRouter roles={getAdminAccessRoleIds()} />}>
                            <Route path="business-insurance/admin" element={<AdminLayout />}>
                                <Route index element={<AdminDashboard />} />
                                <Route path="appetite-mapping" element={<AppetiteMapping />} />
                                <Route path="approval-queue" element={<ApprovalQueue />} />
                                <Route path="audit-logs" element={<AuditLogs />} />
                                <Route path="bulk-operations" element={<BulkOperations />} />
                            </Route>
                        </Route>

                        <Route path="/un-authorised" element={<UnAuthorised />}></Route>
                    </Routes>
                }
            </main>
        </div>
    );
};